<template>
  <a-select
    v-model:value="citySelected"
    size="large"
    :class="['city_select', $attrs.class]"
    :popupClassName="'filter_select_dropdown'"
    :options="filteredCityList"
    @change="onChangeItem"
    placeholder="Location"
  >
    <template #dropdownRender="{ menuNode: menu }">
      <div class="city_search_dropdown">
        <a-input ref="inputRef" v-model:value="citySearch" placeholder="Please enter item" />
        <a-divider style="margin:5px 0" />
        <div v-if="currentCity && filteredCityList.length > 0" class="current_city" @click="onSelectedCurrentCity">
          {{ currentCity }}
        </div>
        <v-nodes :vnodes="menu" />
      </div>
    </template>
    <template #notFoundContent>
      <div class="not_data_wrapper">
        <img src="@/assets/images/no_data.svg" alt="No Data" />
        <div class="not_data_content">No results found for this city</div>
      </div>
    </template>
  </a-select>
</template>

<script setup>
import { message } from 'ant-design-vue';
import { computed, ref, onMounted, defineEmits, defineComponent, defineProps, watch } from 'vue';

import { fetchCityListV2, fetchCurrentCity } from '@/api/common.js';

const props = defineProps({
  isShowAll: {
    type: Boolean,
    default: true,
  },
  selectedCity: {
    type: String,
  },
});

const VNodes = defineComponent({
  props: {
    vnodes: {
      type: Object,
      required: true,
    },
  },
  render() {
    return this.vnodes;
  },
});
const emit = defineEmits(['on-change']);

const currentCity = ref('');
const citySelected = ref();
const citySearch = ref('');
const cityList = ref([]);
const filteredCityList = computed(() => {
  const search = citySearch.value.trim().toLowerCase();
  const base = cityList.value || [];
  const allOption = { value: '', label: 'All' };
  let filtered = base;
  if (props.isShowAll) {
    if (search) {
      filtered = base.filter((item) => item.configValue.toLowerCase().includes(search));
      if (search === 'all') {
        return [allOption, ...filtered.map((item) => ({ value: item.configValue, label: item.configValue }))];
      }
      return filtered.map((item) => ({ value: item.configValue, label: item.configValue }));
    }
    return [allOption, ...base.map((item) => ({ value: item.configValue, label: item.configValue }))];
  } else {
    let filtered = base;
    if (search) {
      filtered = base.filter((item) => item.configValue.toLowerCase().includes(search));
      return filtered.map((item) => ({ value: item.configValue, label: item.configValue }));
    }
    return base.map((item) => ({ value: item.configValue, label: item.configValue }));
  }
});
const getCityList = () => {
  fetchCityListV2(citySearch.value).then((res) => {
    if (res.code == 0) {
      cityList.value = res.data;
    } else {
      message.error(res.msg);
    }
  });
};
watch(citySearch, getCityList, { immediate: true });

const getLocation = () => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;
        getAddress(latitude, longitude);
      },
      (err) => {
        console.error(`Error: ${err.message}`);
      },
      {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 60000,
      }
    );
  } else {
    message.error = 'Geolocation is not supported by this browser.';
  }
};
const getAddress = (latitude, longitude) => {
  fetchCurrentCity(latitude, longitude).then((res) => {
    if (res.error) {
      message.error(res.error);
    } else {
      currentCity.value = res.address.city;
    }
  });
};

const onChangeItem = () => {
  emit('on-change', citySelected.value);
};

const onSelectedCurrentCity = () => {
  citySelected.value = currentCity.value;
  emit('on-change', currentCity.value);
};

onMounted(() => {
  getCityList();
  getLocation();
  citySelected.value = props.selectedCity;
});
</script>

<style lang="less">
.filter_select_dropdown {
  background: #fff;
  min-width: 220px !important;
  padding: 10px;
  .ant-select-item {
    color: #05644f;
    background: #fff;
    &:hover,
    &.ant-select-item-option-active,
    &.ant-select-item-option-selected {
      background: rgba(5, 100, 79, 0.1);
      color: #05644f;
    }
  }
  .ant-input {
    border: 1px solid rgba(5, 100, 79, 0.5);
    color: #05644f;
    border-radius: 16px;
    box-shadow: none;
    &::placeholder {
      color: rgba(5, 100, 79, 0.3);
    }
  }
  .current_city {
    min-height: 32px;
    padding: 5px 12px;
    color: rgba(61, 61, 61, 0.5);
    font-weight: normal;
    font-size: 14px;
    cursor: pointer;
    margin: 5px auto;
  }
  .not_data_wrapper {
    text-align: center;
    img {
      width: 24px;
      height: 24px;
      margin-bottom: 10px;
    }
  }
}
.city_select .ant-select-selection-placeholder {
  color: #05644f;
  line-height: 32px !important;
}
.city_select .ant-select-selector {
  border: 1px solid rgba(5, 100, 79, 0.3) !important;
  box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.03);
  color: #05644f;
  border-radius: 16px;
  height: 48px;
  padding: 0 20px;
  font-size: 14px;
  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    font-size: 14px;
    color: #05644f;
    line-height: 48px !important;
  }
}
.city_select .ant-select-arrow {
  color: #05644f;
}
</style>
